/** Page de callback OIDC — reçoit le code d'autorisation de Keycloak. */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FlotOidcIntrouvable, handleOIDCCallback, startOIDCLogin } from "@/api/v2client";
import { ouvrirSessionLocale } from "@/api/client";

/**
 * Une relance automatique par onglet, pas plus. Sans cette borne, un retour qui ne retrouve
 * jamais son flot (stockage refusé par le navigateur, par exemple) relancerait la connexion
 * en boucle.
 */
const CLE_RELANCE = "infranet_oidc_relance";

export default function OIDCCallback() {
  const [error, setError] = useState<string | null>(null);
  const [params] = useSearchParams();

  useEffect(() => {
    const code = params.get("code");
    const oidcError = params.get("error");
    if (oidcError) {
      setError(`Keycloak : ${oidcError} — ${params.get("error_description") ?? ""}`);
      return;
    }
    if (!code) {
      setError("Code d'autorisation absent de la redirection.");
      return;
    }
    handleOIDCCallback(code, params.get("state"))
      // Le jeton est ÉPROUVÉ avant qu'on déclare la connexion réussie. Sans cette
      // vérification, un jeton que le serveur refuse laisse entrer dans l'application, le
      // premier appel d'API échoue, la session est déclarée perdue et l'écran de connexion
      // revient — sans jamais dire pourquoi. On préfère un message ici, où l'on sait encore
      // ce qui s'est passé.
      .then(async (jeton) => {
        const reponse = await fetch("/api/v1/iam/me", {
          headers: { Authorization: `Bearer ${jeton}` },
        });
        if (!reponse.ok) {
          const corps = await reponse.json().catch(() => null);
          const motif = corps?.errors?.[0]?.message ?? `HTTP ${reponse.status}`;
          throw new Error(`Le serveur a refusé le jeton — ${motif}`);
        }
        // Le périmètre local s'ouvre ICI, et nulle part ailleurs : c'est le seul moment où
        // l'on connaît à la fois le jeton et l'identité derrière. Sans cela, l'écran terrain
        // refusait de télécharger la moindre mission à un utilisateur connecté.
        const moi = await reponse.json().catch(() => null);
        const identifiant = moi?.data?.profile?.username;
        if (identifiant) {
          await ouvrirSessionLocale(identifiant, jeton);
        }
        sessionStorage.removeItem(CLE_RELANCE);
        // Rechargement complet, et non `navigate()` : la garde d'authentification vit
        // AU-DESSUS du routeur, dans `App`. Une navigation interne ne la ferait pas
        // reconsidérer son verdict — on resterait sur l'écran de connexion, un jeton valide
        // en poche. Le rechargement la fait relire le coffre une fois, proprement, et laisse
        // une page d'entrée dont l'adresse ne porte plus le code d'autorisation.
        window.location.replace("/");
      })
      .catch((e) => {
        // Un retour sans flot connu (autre onglet, lien d'e-mail Keycloak, adresse rouverte)
        // n'est pas un échec de l'utilisateur : sa session Keycloak est ouverte. Relancer la
        // connexion le ramène ici avec un code neuf, sans rien ressaisir.
        if (e instanceof FlotOidcIntrouvable && !sessionStorage.getItem(CLE_RELANCE)) {
          sessionStorage.setItem(CLE_RELANCE, "1");
          void startOIDCLogin();
          return;
        }
        sessionStorage.removeItem(CLE_RELANCE);
        setError(e instanceof Error ? e.message : "Échec de l'échange OIDC.");
      });
  }, [params]);

  if (error) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <h2>Connexion impossible</h2>
        <p style={{ color: "#d63939" }}>{error}</p>
        <button onClick={() => void startOIDCLogin()}>Recommencer la connexion</button>
      </div>
    );
  }
  return (
    <div style={{ padding: 24, textAlign: "center" }}>
      <p>Authentification en cours…</p>
    </div>
  );
}
